import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  Surface,
  VerticalStack,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import { listLinkedSellers, PartnerLinkError, type PartnerGrantRecord } from '@/lib/partner-links';
import { CommerceRegistrySearchField } from './CommerceRegistrySearchField';

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'requested':
      return 'warning';
    case 'rejected':
    case 'revoked':
      return 'muted';
    default:
      return 'default';
  }
}

export default function LinkedSellersPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [linkedSellers, setLinkedSellers] = useState<PartnerGrantRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [totalLinkedSellers, setTotalLinkedSellers] = useState(0);
  const query = useCommerceRegistryQuery({
    facetKeys: [],
    sortFields: ['created_at', 'granting_tenant_name', 'updated_at'],
  });
  const requestSequence = useRef(0);

  useEffect(() => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      setLinkedSellers([]);
      setTotalLinkedSellers(0);
      setLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    void listLinkedSellers(activeTenant.id, {
      search: query.search || undefined,
      sort: query.sort,
      ...toLimitOffset(query.pagination),
    })
      .then(page => {
        if (requestId !== requestSequence.current) return;
        const nextPageIndex = lastPageIndexForTotal(page.total_count, query.pagination.pageSize);
        if (query.pagination.pageIndex > nextPageIndex) {
          query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
          return;
        }
        setLinkedSellers(page.items);
        setTotalLinkedSellers(page.total_count);
      })
      .catch(err => {
        if (requestId !== requestSequence.current) return;
        if (err instanceof PartnerLinkError && (err.status === 401 || err.status === 403)) {
          setAccessDeniedStatus(err.status);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load linked sellers.');
        }
        setLinkedSellers([]);
        setTotalLinkedSellers(0);
      })
      .finally(() => {
        if (requestId === requestSequence.current) setLoading(false);
      });
  }, [activeTenant?.id, query.pagination, query.search, query.sort]);

  const shellViewModel = useMemo(
    () =>
      buildCommerceShellViewModel({
        activeTenant,
        accessDeniedStatus,
        activeSectionId: 'linked_sellers',
        searchQuery: query.search,
        searchPlaceholder: 'Search linked sellers...',
        onSearchChange: query.setSearch,
        error,
        blockingReason: 'Your current role does not have permission to inspect linked sellers.',
        emptyStateLabel: 'Select a reseller tenant to inspect approved seller relationships.',
        onSelectSection: sectionId => {
          if (sectionId === 'linked_sellers') {
            navigation.navigate('/reseller/linked-sellers');
            return;
          }
          if (sectionId === 'reseller_entitlements') {
            navigation.navigate('/reseller/entitlements');
            return;
          }
          if (sectionId === 'carts') {
            navigation.navigate('/reseller/carts');
            return;
          }
          if (sectionId === 'orders') {
            navigation.navigate('/reseller/orders');
            return;
          }
          if (sectionId === 'commission_ledger') {
            navigation.navigate('/reseller/commission-ledger');
            return;
          }
          navigation.navigate(buildCommerceSectionRoute(sectionId));
        },
        primaryAction: {
          id: 'reseller-entitlements-route',
          label: 'View Entitlements',
          disabled: false,
          onAction: () => navigation.navigate('/reseller/entitlements'),
        },
        metrics: [
          { id: 'linked-sellers', label: 'Linked Sellers', value: String(totalLinkedSellers) },
        ],
      }),
    [accessDeniedStatus, activeTenant, error, navigation, query.search, query.setSearch, totalLinkedSellers],
  );

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      breadcrumb={<CommerceBreadcrumbHeader items={[{ label: 'Agent', href: '/reseller/entitlements' }, { label: 'Linked Sellers' }]} />}
    >
      <VerticalStack gap={6} className="p-6">
        <Surface className="border-border/60 p-5">
          <VerticalStack gap={4}>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-muted-foreground/60">Linked Sellers</p>
              <DescriptionText className="text-sm">
                Seller tenants that have approved a tenant-to-tenant visibility link for this reseller tenant. This does not grant direct access to seller-private workspaces.
              </DescriptionText>
            </div>
            <CommerceRegistrySearchField
              value={query.search}
              onChange={query.setSearch}
              placeholder="Search all linked sellers..."
              resultCount={totalLinkedSellers}
            />
            {loading ? (
              <RegistryStatus label="Loading" variant="default" />
            ) : linkedSellers.length === 0 ? (
              <div className="py-4 text-center">
                <DescriptionText className="text-sm">
                  {query.search
                    ? 'No linked sellers matched the current search.'
                    : 'No seller tenants have linked this reseller tenant yet.'}
                </DescriptionText>
                {query.search ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search
                  </button>
                ) : null}
              </div>
            ) : (
              <VerticalStack gap={3}>
                {linkedSellers.map(item => (
                  <Surface key={item.id} className="border-border/50 p-4">
                    <HorizontalStack className="items-start justify-between gap-4">
                      <VerticalStack gap={2}>
                        <p className="text-sm font-black text-foreground">{item.grantingTenantName || item.grantingTenantSlug || item.grantingTenantId}</p>
                        <DescriptionText className="text-xs">
                          Seller {item.grantingTenantSlug || item.grantingTenantId}
                        </DescriptionText>
                      </VerticalStack>
                      <RegistryStatus label={item.status} variant={statusVariant(item.status)} />
                    </HorizontalStack>
                  </Surface>
                ))}
              </VerticalStack>
            )}
            <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-3">
              <button
                type="button"
                onClick={() => query.onPaginationChange(current => ({ ...current, pageIndex: Math.max(0, current.pageIndex - 1) }))}
                disabled={loading || query.pagination.pageIndex === 0}
                className="rounded-md border border-border/60 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                {query.pagination.pageIndex + 1} / {lastPageIndexForTotal(totalLinkedSellers, query.pagination.pageSize) + 1}
              </span>
              <button
                type="button"
                onClick={() => query.onPaginationChange(current => ({ ...current, pageIndex: current.pageIndex + 1 }))}
                disabled={loading || query.pagination.pageIndex >= lastPageIndexForTotal(totalLinkedSellers, query.pagination.pageSize)}
                className="rounded-md border border-border/60 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </VerticalStack>
        </Surface>
      </VerticalStack>
    </CommerceCatalogWorkspace>
  );
}
